import Vue from 'vue'
import { isSamePath as _isSamePath, joinURL, normalizeURL, withQuery, withoutTrailingSlash } from 'ufo'

// window.{{globals.loadedCallback}} hook
// Useful for jsdom testing or plugins (https://github.com/tmpvar/jsdom#dealing-with-asynchronous-script-loading)
if (process.client) {
  window.onNuxtReadyCbs = []
  window.onNuxtReady = (cb) => {
    window.onNuxtReadyCbs.push(cb)
  }
}

export function createGetCounter (counterObject, defaultKey = '') {
  return function getCounter (id = defaultKey) {
    if (counterObject[id] === undefined) {
      counterObject[id] = 0
    }
    return counterObject[id]++
  }
}

export function empty () {}

export function globalHandleError (error) {
  if (Vue.config.errorHandler) {
    Vue.config.errorHandler(error)
  }
}

export function interopDefault (promise) {
  return promise.then(m => m.default || m)
}

export function hasFetch(vm) {
  return vm.$options && typeof vm.$options.fetch === 'function' && !vm.$options.fetch.length
}
export function purifyData(data) {
  if (process.env.NODE_ENV === 'production') {
    return data
  }

  return Object.entries(data).filter(
    ([key, value]) => {
      const valid = !(value instanceof Function) && !(value instanceof Promise)
      if (!valid) {
        console.warn(`${key} is not able to be stringified. This will break in a production environment.`)
      }
      return valid
    }
    ).reduce((obj, [key, value]) => {
      obj[key] = value
      return obj
    }, {})
}
export function getChildrenComponentInstancesUsingFetch(vm, instances = []) {
  const children = vm.$children || []
  for (const child of children) {
    if (child.$fetch) {
      instances.push(child)
    }
    if (child.$children) {
      getChildrenComponentInstancesUsingFetch(child, instances)
    }
  }
  return instances
}

export function applyAsyncData (Component, asyncData) {
  if (
    // For SSR, we once all this function without second param to just apply asyncData
    // Prevent doing this for each SSR request
    !asyncData && Component.options.__hasNuxtData
  ) {
    return
  }

  const ComponentData = Component.options._originDataFn || Component.options.data || function () { return {} }
  Component.options._originDataFn = ComponentData

  Component.options.data = function () {
    const data = ComponentData.call(this, this)
    if (this.$ssrContext) {
      asyncData = this.$ssrContext.asyncData[Component.cid]
    }
    return { ...data, ...asyncData }
  }

  Component.options.__hasNuxtData = true

  if (Component._Ctor && Component._Ctor.options) {
    Component._Ctor.options.data = Component.options.data
  }
}

export function sanitizeComponent (Component) {
  // If Component already sanitized
  if (Component.options && Component._Ctor === Component) {
    return Component
  }
  if (!Component.options) {
    Component = Vue.extend(Component) // fix issue #6
    Component._Ctor = Component
  } else {
    Component._Ctor = Component
    Component.extendOptions = Component.options
  }
  // If no component name defined, set file path as name, (also fixes #5703)
  if (!Component.options.name && Component.options.__file) {
    Component.options.name = Component.options.__file
  }
  return Component
}

export function getMatchedComponents (route, matches = false, prop = 'components') {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m[prop]).map((key) => {
      matches && matches.push(index)
      return m[prop][key]
    })
  }))
}

export function getMatchedComponentsInstances (route, matches = false) {
  return getMatchedComponents(route, matches, 'instances')
}

export function flatMapComponents (route, fn) {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m.components).reduce((promises, key) => {
      if (m.components[key]) {
        promises.push(fn(m.components[key], m.instances[key], m, key, index))
      } else {
        delete m.components[key]
      }
      return promises
    }, [])
  }))
}

export function resolveRouteComponents (route, fn) {
  return Promise.all(
    flatMapComponents(route, async (Component, instance, match, key) => {
      // If component is a function, resolve it
      if (typeof Component === 'function' && !Component.options) {
        try {
          Component = await Component()
        } catch (error) {
          // Handle webpack chunk loading errors
          // This may be due to a new deployment or a network problem
          if (
            error &&
            error.name === 'ChunkLoadError' &&
            typeof window !== 'undefined' &&
            window.sessionStorage
          ) {
            const timeNow = Date.now()
            try {
              const previousReloadTime = parseInt(window.sessionStorage.getItem('nuxt-reload'))
              // check for previous reload time not to reload infinitely
              if (!previousReloadTime || previousReloadTime + 60000 < timeNow) {
                window.sessionStorage.setItem('nuxt-reload', timeNow)
                window.location.reload(true /* skip cache */)
              }
            } catch {
              // don't throw an error if we have issues reading sessionStorage
            }
          }

          throw error
        }
      }
      match.components[key] = Component = sanitizeComponent(Component)
      return typeof fn === 'function' ? fn(Component, instance, match, key) : Component
    })
  )
}

export async function getRouteData (route) {
  if (!route) {
    return
  }
  // Make sure the components are resolved (code-splitting)
  await resolveRouteComponents(route)
  // Send back a copy of route with meta based on Component definition
  return {
    ...route,
    meta: getMatchedComponents(route).map((Component, index) => {
      return { ...Component.options.meta, ...(route.matched[index] || {}).meta }
    })
  }
}

export async function setContext (app, context) {
  // If context not defined, create it
  if (!app.context) {
    app.context = {
      isStatic: process.static,
      isDev: false,
      isHMR: false,
      app,
      store: app.store,
      payload: context.payload,
      error: context.error,
      base: app.router.options.base,
      env: {"CURRENT_APP_VERSION":"1.0.2","CURRENT_APP_BRANCH":"origin/md/3491","BUILD_MODE":"staging","LESSOPEN":"| /usr/bin/lesspipe %s","npm_package_dependencies_resize_observer_polyfill":"^1.5.1","npm_package_dependencies_caniuse_lite":"^1.0.30001615","npm_package_dependencies__nuxtjs_style_resources":"^1.0.0","npm_package_scripts_dev_staging":"nuxt --dotenv .env.staging","npm_package_dependencies_vue2_gmap_custom_marker":"^6.0.1","USER":"ubuntu","SSH_CLIENT":"27.72.89.216 52612 22","npm_config_version_commit_hooks":"true","npm_config_user_agent":"yarn/1.22.22 npm/? node/v20.17.0 linux x64","npm_package_dependencies_chartjs_plugin_gradient":"^0.5.1","npm_package_scripts_setup_production":"./node_modules/.bin/pm2 deploy production setup","npm_config_bin_links":"true","XDG_SESSION_TYPE":"tty","npm_node_execpath":"/home/ubuntu/.nvm/versions/node/v20.17.0/bin/node","npm_package_dependencies_v_calendar":"^2.3.4","npm_config_init_version":"1.0.0","SHLVL":"1","npm_package_dependencies__amplitude_analytics_browser":"^2.0.0","MOTD_SHOWN":"pam","HOME":"/home/ubuntu","OLDPWD":"/home/ubuntu/podfoods-website/current","npm_package_dependencies_webp_converter":"^2.3.3","npm_package_dependencies_chart_js":"^3.4.1","npm_package_dependencies__contentful_rich_text_html_renderer":"^16.0.3","npm_package_scripts_deploy_autotest":"./node_modules/.bin/pm2 deploy autotest update","NVM_BIN":"/home/ubuntu/.nvm/versions/node/v20.17.0/bin","npm_package_scripts_setup_dev":"./node_modules/.bin/pm2 deploy dev setup","PYENV_SHELL":"bash","NVM_INC":"/home/ubuntu/.nvm/versions/node/v20.17.0/include/node","npm_package_dependencies__fontsource_open_sauce_sans":"^5.2.5","npm_package_scripts_setup_staging":"./node_modules/.bin/pm2 deploy staging setup","npm_package_scripts_dev_local":"nuxt --dotenv .env.local","npm_package_engines_node":"20.17.0","npm_config_init_license":"MIT","NODE_OPTIONS":"--max_old_space_size=1024","YARN_WRAP_OUTPUT":"false","npm_package_dependencies_nuxt_compress":"^5.0.0","npm_package_dependencies__nuxtjs_sitemap":"^2.4.0","npm_package_scripts_build_elements":"./node_modules/.bin/sass --quiet --load-path=./node_modules --style=compressed assets/elements.scss > assets/elements.css","npm_config_version_tag_prefix":"v","LC_CTYPE":"UTF-8","npm_package_dependencies_vue_pivottable":"^0.4.65","npm_package_dependencies_eslint":"^7.11.0","npm_package_dependencies__splinetool_runtime":"^1.9.82","npm_package_scripts_lint_fix":"eslint --fix --ext .js,.vue --ignore-path .gitignore .","npm_package_scripts_generate_webp":"node webp.js","npm_package_scripts_dev_rc":"nuxt --dotenv .env.rc","DBUS_SESSION_BUS_ADDRESS":"unix:path=/run/user/1000/bus","npm_package_dependencies_vue_image_zoomer":"1.7.1","npm_package_dependencies_babel_plugin_lodash":"^3.3.4","npm_package_dependencies__nuxtjs_gtm":"^2.4.0","npm_package_dependencies_gmap_vue":"^1.5.0","npm_package_dependencies_chartjs_adapter_moment":"^1.0.0","npm_package_description":"podfoods.co","NVM_DIR":"/home/ubuntu/.nvm","npm_package_readmeFilename":"README.md","npm_package_dependencies_vue_qr":"^4.0.9","npm_package_dependencies_vue_awesome_swiper":"^4.1.1","npm_package_dependencies_nuxt":"2.18.1","npm_package_dependencies_vue_lottie":"^0.2.1","npm_package_dependencies_current_git_branch":"^1.1.0","npm_package_scripts_dev":"nuxt --dotenv .env.development","LOGNAME":"ubuntu","_":"/home/ubuntu/.nvm/versions/node/v20.17.0/bin/yarn","npm_package_dependencies_vue_social_sharing":"^3.0.9","npm_package_dependencies_sass_loader":"^10.4.1","npm_package_dependencies_pm2":"^5.4.2","npm_package_dependencies_express":"^4.20.0","npm_package_dependencies_contentful":"^9.3.5","npm_package_private":"true","XDG_SESSION_CLASS":"user","npm_package_dependencies_nuxt_jsonld":"^1.4.5","npm_package_scripts_deploy_production":"./node_modules/.bin/pm2 deploy production update","npm_package_scripts_lint":"eslint --ext .js,.vue --ignore-path .gitignore .","npm_config_registry":"https://registry.yarnpkg.com","XDG_SESSION_ID":"37178","npm_package_dependencies__nuxtjs_google_analytics":"^2.4.0","npm_package_scripts_setup_rc":"./node_modules/.bin/pm2 deploy rc setup","npm_package_dependencies__nuxtjs_svg":"^0.1.12","npm_package_scripts_start":"nuxt start","npm_config_ignore_scripts":"","PATH":"/tmp/yarn--1746417328008-0.36331409608026544:/home/ubuntu/podfoods-website/source/node_modules/.bin:/home/ubuntu/.config/yarn/link/node_modules/.bin:/home/ubuntu/.yarn/bin:/home/ubuntu/.nvm/versions/node/v20.17.0/libexec/lib/node_modules/npm/bin/node-gyp-bin:/home/ubuntu/.nvm/versions/node/v20.17.0/lib/node_modules/npm/bin/node-gyp-bin:/home/ubuntu/.nvm/versions/node/v20.17.0/bin/node_modules/npm/bin/node-gyp-bin:/home/ubuntu/.pyenv/shims:/home/ubuntu/.pyenv/bin:/home/ubuntu/.nvm/versions/node/v20.17.0/bin:/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin:/usr/games:/usr/local/games:/snap/bin","NODE":"/home/ubuntu/.nvm/versions/node/v20.17.0/bin/node","npm_package_dependencies_vuex_persist":"^3.1.3","npm_package_dependencies_swiper":"^6.4.1","npm_package_dependencies_file_saver":"^2.0.5","npm_package_dependencies_eslint_plugin_vue":"^7.0.1","npm_package_dependencies__googlemaps_js_api_loader":"^1.12.8","npm_package_scripts_deploy_dev":"./node_modules/.bin/pm2 deploy dev update","npm_package_scripts_forward_agent":"ssh-agent bash && ssh-add","npm_package_name":"frontend-ssr","XDG_RUNTIME_DIR":"/run/user/1000","npm_package_dependencies_marked":"^4.0.10","npm_package_dependencies_babel_eslint":"^10.1.0","npm_package_scripts_deploy_staging":"./node_modules/.bin/pm2 deploy staging update","npm_package_dependencies_jsbarcode":"^3.11.5","npm_package_scripts_dev_autotest":"nuxt --dotenv .env.autotest","LANG":"C.UTF-8","npm_package_devDependencies__nuxtjs_eslint_config":"^8.0.0","LS_COLORS":"","npm_lifecycle_script":"nuxt build","npm_package_dependencies__contentful_rich_text_plain_text_renderer":"^16.0.3","SSH_AUTH_SOCK":"/tmp/ssh-XXXXuEmXs6/agent.591535","npm_package_dependencies_pug_plain_loader":"^1.0.0","npm_package_dependencies_body_parser":"^1.19.0","npm_config_version_git_message":"v%s","SHELL":"/bin/bash","npm_lifecycle_event":"build","npm_package_dependencies_vuedraggable":"^2.24.3","npm_package_dependencies_jquery":"^3.6.0","npm_package_dependencies_exceljs":"^4.3.0","npm_package_dependencies__nuxtjs_moment":"^1.6.1","npm_package_dependencies__nuxtjs_dotenv":"^1.4.1","npm_package_version":"1.0.2","npm_config_argv":"{\"remain\":[],\"cooked\":[\"run\",\"build\"],\"original\":[\"build\"]}","npm_package_dependencies_element_ui":"^2.15.1","npm_package_dependencies_cookie_universal_nuxt":"^2.1.5","npm_package_scripts_build":"nuxt build","LESSCLOSE":"/usr/bin/lesspipe %s %s","npm_package_dependencies_vue_markdown":"^2.2.4","npm_package_dependencies_lodash":"^4.17.21","npm_package_dependencies_boxicons":"^2.0.9","npm_package_dependencies_aos":"^3.0.0-beta.6","npm_package_dependencies_vue_chartjs":"^4.1.2","npm_package_dependencies_nuxt_stripe_module":"^3.1.7","npm_package_dependencies_actioncable_vue":"^2.5.1","npm_config_version_git_tag":"true","npm_config_version_git_sign":"","npm_package_license":"MIT","npm_package_dependencies_sass":"^1.77.8","npm_package_dependencies_eslint_plugin_nuxt":"^1.0.0","npm_package_scripts_setup_autotest":"./node_modules/.bin/pm2 deploy autotest setup","npm_package_scripts_generate":"nuxt generate","npm_config_strict_ssl":"true","npm_package_scripts_deploy_rc":"./node_modules/.bin/pm2 deploy rc update","PWD":"/home/ubuntu/podfoods-website/current","npm_execpath":"/home/ubuntu/.nvm/versions/node/v20.17.0/lib/node_modules/yarn/bin/yarn.js","npm_package_dependencies_numeral":"^2.0.6","npm_package_dependencies__datadog_browser_rum":"^4.7.0","SSH_CONNECTION":"27.72.89.216 52612 10.0.2.102 22","NVM_CD_FLAGS":"","PYENV_ROOT":"/home/ubuntu/.pyenv","npm_package_dependencies_eslint_loader":"^4.0.2","npm_package_scripts_dev_production":"nuxt --dotenv .env.production","npm_package_author_name":"Gia ←gia@coa.vn→","npm_package_dependencies_v_tooltip":"^2.0.3","npm_config_save_prefix":"^","npm_config_ignore_optional":"","npm_package_dependencies_chartjs_adapter_date_fns":"^2.0.0","npm_package_dependencies__nuxtjs_robots":"^2.5.0","npm_package_dependencies__nuxtjs_axios":"^5.13.6","npm_package_dependencies_vue_signature_pad":"2.0.5","INIT_CWD":"/home/ubuntu/podfoods-website/source","npm_package_dependencies_pug":"^3.0.3","npm_package_dependencies_chartjs_plugin_datalabels":"^2.2.0","NODE_ENV":"production","_applied":"true","WEBSITE_BASE_URL":"https://beta.podfoods.co","WEBSITE_LP_BASE_URL":"https://lp.beta.podfoods.co","WEBSITE_BROKER_BASE_URL":"https://broker.beta.podfoods.co","API_BASE_URL":"https://apibeta.podfoods.co","SOCKET_BASE_URL":"wss://apibeta.podfoods.co/cable","CAPITAL_S3_KANMON_DOCS_BUCKET":"pfcapital-kanmon-docs-staging","CONTENTFUL_CDA_ACCESS_TOKEN":"zZPeXJV37EjTLWsUlomiuVbqac5DMrX0Levn2ZTD3RM","CONTENTFUL_ENVIRONMENT":"staging"}
    }
    // Only set once

    if (context.req) {
      app.context.req = context.req
    }
    if (context.res) {
      app.context.res = context.res
    }

    if (context.ssrContext) {
      app.context.ssrContext = context.ssrContext
    }
    app.context.redirect = (status, path, query) => {
      if (!status) {
        return
      }
      app.context._redirected = true
      // if only 1 or 2 arguments: redirect('/') or redirect('/', { foo: 'bar' })
      let pathType = typeof path
      if (typeof status !== 'number' && (pathType === 'undefined' || pathType === 'object')) {
        query = path || {}
        path = status
        pathType = typeof path
        status = 302
      }
      if (pathType === 'object') {
        path = app.router.resolve(path).route.fullPath
      }
      // "/absolute/route", "./relative/route" or "../relative/route"
      if (/(^[.]{1,2}\/)|(^\/(?!\/))/.test(path)) {
        app.context.next({
          path,
          query,
          status
        })
      } else {
        path = withQuery(path, query)
        if (process.server) {
          app.context.next({
            path,
            status
          })
        }
        if (process.client) {
          // https://developer.mozilla.org/en-US/docs/Web/API/Location/assign
          window.location.assign(path)

          // Throw a redirect error
          throw new Error('ERR_REDIRECT')
        }
      }
    }
    if (process.server) {
      app.context.beforeNuxtRender = fn => context.beforeRenderFns.push(fn)
      app.context.beforeSerialize = fn => context.beforeSerializeFns.push(fn)
    }
    if (process.client) {
      app.context.nuxtState = window.__NUXT__
    }
  }

  // Dynamic keys
  const [currentRouteData, fromRouteData] = await Promise.all([
    getRouteData(context.route),
    getRouteData(context.from)
  ])

  if (context.route) {
    app.context.route = currentRouteData
  }

  if (context.from) {
    app.context.from = fromRouteData
  }

  if (context.error) {
    app.context.error = context.error
  }

  app.context.next = context.next
  app.context._redirected = false
  app.context._errored = false
  app.context.isHMR = false
  app.context.params = app.context.route.params || {}
  app.context.query = app.context.route.query || {}
}

export function middlewareSeries (promises, appContext, renderState) {
  if (!promises.length || appContext._redirected || appContext._errored || (renderState && renderState.aborted)) {
    return Promise.resolve()
  }
  return promisify(promises[0], appContext)
    .then(() => {
      return middlewareSeries(promises.slice(1), appContext, renderState)
    })
}

export function promisify (fn, context) {
  let promise
  if (fn.length === 2) {
    // fn(context, callback)
    promise = new Promise((resolve) => {
      fn(context, function (err, data) {
        if (err) {
          context.error(err)
        }
        data = data || {}
        resolve(data)
      })
    })
  } else {
    promise = fn(context)
  }

  if (promise && promise instanceof Promise && typeof promise.then === 'function') {
    return promise
  }
  return Promise.resolve(promise)
}

// Imported from vue-router
export function getLocation (base, mode) {
  if (mode === 'hash') {
    return window.location.hash.replace(/^#\//, '')
  }

  base = decodeURI(base).slice(0, -1) // consideration is base is normalized with trailing slash
  let path = decodeURI(window.location.pathname)

  if (base && path.startsWith(base)) {
    path = path.slice(base.length)
  }

  const fullPath = (path || '/') + window.location.search + window.location.hash

  return normalizeURL(fullPath)
}

// Imported from path-to-regexp

/**
 * Compile a string to a template function for the path.
 *
 * @param  {string}             str
 * @param  {Object=}            options
 * @return {!function(Object=, Object=)}
 */
export function compile (str, options) {
  return tokensToFunction(parse(str, options), options)
}

export function getQueryDiff (toQuery, fromQuery) {
  const diff = {}
  const queries = { ...toQuery, ...fromQuery }
  for (const k in queries) {
    if (String(toQuery[k]) !== String(fromQuery[k])) {
      diff[k] = true
    }
  }
  return diff
}

export function normalizeError (err) {
  let message
  if (!(err.message || typeof err === 'string')) {
    try {
      message = JSON.stringify(err, null, 2)
    } catch (e) {
      message = `[${err.constructor.name}]`
    }
  } else {
    message = err.message || err
  }
  return {
    ...err,
    message,
    statusCode: (err.statusCode || err.status || (err.response && err.response.status) || 500)
  }
}

/**
 * The main path matching regexp utility.
 *
 * @type {RegExp}
 */
const PATH_REGEXP = new RegExp([
  // Match escaped characters that would otherwise appear in future matches.
  // This allows the user to escape special characters that won't transform.
  '(\\\\.)',
  // Match Express-style parameters and un-named parameters with a prefix
  // and optional suffixes. Matches appear as:
  //
  // "/:test(\\d+)?" => ["/", "test", "\d+", undefined, "?", undefined]
  // "/route(\\d+)"  => [undefined, undefined, undefined, "\d+", undefined, undefined]
  // "/*"            => ["/", undefined, undefined, undefined, undefined, "*"]
  '([\\/.])?(?:(?:\\:(\\w+)(?:\\(((?:\\\\.|[^\\\\()])+)\\))?|\\(((?:\\\\.|[^\\\\()])+)\\))([+*?])?|(\\*))'
].join('|'), 'g')

/**
 * Parse a string for the raw tokens.
 *
 * @param  {string}  str
 * @param  {Object=} options
 * @return {!Array}
 */
function parse (str, options) {
  const tokens = []
  let key = 0
  let index = 0
  let path = ''
  const defaultDelimiter = (options && options.delimiter) || '/'
  let res

  while ((res = PATH_REGEXP.exec(str)) != null) {
    const m = res[0]
    const escaped = res[1]
    const offset = res.index
    path += str.slice(index, offset)
    index = offset + m.length

    // Ignore already escaped sequences.
    if (escaped) {
      path += escaped[1]
      continue
    }

    const next = str[index]
    const prefix = res[2]
    const name = res[3]
    const capture = res[4]
    const group = res[5]
    const modifier = res[6]
    const asterisk = res[7]

    // Push the current path onto the tokens.
    if (path) {
      tokens.push(path)
      path = ''
    }

    const partial = prefix != null && next != null && next !== prefix
    const repeat = modifier === '+' || modifier === '*'
    const optional = modifier === '?' || modifier === '*'
    const delimiter = res[2] || defaultDelimiter
    const pattern = capture || group

    tokens.push({
      name: name || key++,
      prefix: prefix || '',
      delimiter,
      optional,
      repeat,
      partial,
      asterisk: Boolean(asterisk),
      pattern: pattern ? escapeGroup(pattern) : (asterisk ? '.*' : '[^' + escapeString(delimiter) + ']+?')
    })
  }

  // Match any characters still remaining.
  if (index < str.length) {
    path += str.substr(index)
  }

  // If the path exists, push it onto the end.
  if (path) {
    tokens.push(path)
  }

  return tokens
}

/**
 * Prettier encoding of URI path segments.
 *
 * @param  {string}
 * @return {string}
 */
function encodeURIComponentPretty (str, slashAllowed) {
  const re = slashAllowed ? /[?#]/g : /[/?#]/g
  return encodeURI(str).replace(re, (c) => {
    return '%' + c.charCodeAt(0).toString(16).toUpperCase()
  })
}

/**
 * Encode the asterisk parameter. Similar to `pretty`, but allows slashes.
 *
 * @param  {string}
 * @return {string}
 */
function encodeAsterisk (str) {
  return encodeURIComponentPretty(str, true)
}

/**
 * Escape a regular expression string.
 *
 * @param  {string} str
 * @return {string}
 */
function escapeString (str) {
  return str.replace(/([.+*?=^!:${}()[\]|/\\])/g, '\\$1')
}

/**
 * Escape the capturing group by escaping special characters and meaning.
 *
 * @param  {string} group
 * @return {string}
 */
function escapeGroup (group) {
  return group.replace(/([=!:$/()])/g, '\\$1')
}

/**
 * Expose a method for transforming tokens into the path function.
 */
function tokensToFunction (tokens, options) {
  // Compile all the tokens into regexps.
  const matches = new Array(tokens.length)

  // Compile all the patterns before compilation.
  for (let i = 0; i < tokens.length; i++) {
    if (typeof tokens[i] === 'object') {
      matches[i] = new RegExp('^(?:' + tokens[i].pattern + ')$', flags(options))
    }
  }

  return function (obj, opts) {
    let path = ''
    const data = obj || {}
    const options = opts || {}
    const encode = options.pretty ? encodeURIComponentPretty : encodeURIComponent

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]

      if (typeof token === 'string') {
        path += token

        continue
      }

      const value = data[token.name || 'pathMatch']
      let segment

      if (value == null) {
        if (token.optional) {
          // Prepend partial segment prefixes.
          if (token.partial) {
            path += token.prefix
          }

          continue
        } else {
          throw new TypeError('Expected "' + token.name + '" to be defined')
        }
      }

      if (Array.isArray(value)) {
        if (!token.repeat) {
          throw new TypeError('Expected "' + token.name + '" to not repeat, but received `' + JSON.stringify(value) + '`')
        }

        if (value.length === 0) {
          if (token.optional) {
            continue
          } else {
            throw new TypeError('Expected "' + token.name + '" to not be empty')
          }
        }

        for (let j = 0; j < value.length; j++) {
          segment = encode(value[j])

          if (!matches[i].test(segment)) {
            throw new TypeError('Expected all "' + token.name + '" to match "' + token.pattern + '", but received `' + JSON.stringify(segment) + '`')
          }

          path += (j === 0 ? token.prefix : token.delimiter) + segment
        }

        continue
      }

      segment = token.asterisk ? encodeAsterisk(value) : encode(value)

      if (!matches[i].test(segment)) {
        throw new TypeError('Expected "' + token.name + '" to match "' + token.pattern + '", but received "' + segment + '"')
      }

      path += token.prefix + segment
    }

    return path
  }
}

/**
 * Get the flags for a regexp from the options.
 *
 * @param  {Object} options
 * @return {string}
 */
function flags (options) {
  return options && options.sensitive ? '' : 'i'
}

export function addLifecycleHook(vm, hook, fn) {
  if (!vm.$options[hook]) {
    vm.$options[hook] = []
  }
  if (!vm.$options[hook].includes(fn)) {
    vm.$options[hook].push(fn)
  }
}

export const urlJoin = joinURL

export const stripTrailingSlash = withoutTrailingSlash

export const isSamePath = _isSamePath

export function setScrollRestoration (newVal) {
  try {
    window.history.scrollRestoration = newVal;
  } catch(e) {}
}
